import { featureFlagService } from '@superbet-group/web.lib.flags';
import {
    isBeforeDate,
    subtractDays,
} from '@superbet-group/web.lib.utilities';
import { Storage } from '@superbet-group/web.lib.vue-utilities';
import {
    pick as _pick,
    isEmpty as _isEmpty,
} from 'lodash';
import { getCookie } from '@app/utils/cookieHelpers';

import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';

import {
    expiresDaysMarketingSourceTags,
    BTAG_SET_DATE,
    btagQueryParams,
    utmQueryParams,
    MARKETING_DATA,
    marketingAttributionData,
    AcquisitionSourcesLength,
} from './_private/constants';
import {
    BtagData,
    UtmData,
    MarketingData,
    QueryDict,
    UtmQueryParam,
    BtagQueryParam,
    MarketingAttributionData,
    AssembledMarketingAttributionData,
    AttributionCustomParam,
    GetQuery,
} from './_private/types';

export class MarketingSourceService {
    private storage: Storage;
    private postViewData: MarketingData = {
        btag: '',
        affid: '',
        utm_medium: '',
        utm_source: '',
        utm_campaign: '',
        utm_content: '',
        utm_term: '',
    };

    constructor(storage: Storage = clientStorageWithExpiry) {
        this.storage = storage;
    }

    public async handleMarketingQueryParams(query: QueryDict<UtmQueryParam & BtagQueryParam>): Promise<void> {
        let urlTags: BtagData & UtmData = _pick(query, [
            ...btagQueryParams,
            ...utmQueryParams,
        ]);
        if (!_isEmpty(urlTags)) {
            this.clearMarketingData();
            await this.setMarketingData(urlTags);
        }
        if (this.isBtagExpired()) {
            this.clearMarketingData();
        }
    }

    public async handleMarketingAttributionQueryParams(query: QueryDict<UtmQueryParam & BtagQueryParam>): Promise<void> {
        let urlTags: BtagData & UtmData = _pick(query, [
            ...btagQueryParams,
            ...utmQueryParams,
        ]);

        if (urlTags.btag && urlTags.utm_source?.toLowerCase() !== 'affiliate') {
            urlTags = _pick(urlTags, [
                ...btagQueryParams,
            ]);
        }

        if (urlTags.affid) {
            urlTags.utm_medium = urlTags.affid;
            delete urlTags.affid;
        }

        const queryData = MarketingSourceService.getQuery(urlTags);

        if (queryData.isActionable) {
            await this.setMarketingAttributionData(queryData.queryToString);
        }
    }

    public getMarketingData(): MarketingData | Record<string, never> {
        return this.storage.get<MarketingData>(MARKETING_DATA)!;
    }

    public getMarketingAttributionData(): MarketingAttributionData | null {
        return this.storage.get<MarketingAttributionData>(marketingAttributionData) || null;
    }

    public getAssembledMarketingAttributionData(actionName: string):AssembledMarketingAttributionData {
        const data = this.getMarketingAttributionData();
        if (!data) {
            return {};
        }

        const customParameters: AttributionCustomParam[] = data.map((item, index) => {
            return {
                Key: actionName + index,
                Value: item,
            };
        });
        return { customParameters };
    }

    public clearMarketingData(): void {
        this.storage.remove(MARKETING_DATA);
        this.storage.remove(BTAG_SET_DATE);
    }

    private async setMarketingData(inputTags: BtagData & UtmData): Promise<void> {
        let tags = { ...inputTags };
        const shouldSendUtmsAndBtagsTogether = await featureFlagService.getFeatureValue('growth.should-send-utms-btags-together', false);

        if (tags.btag) {
            // btag data should be removed if utmData exist but utm_source isn't affiliate
            if (tags.utm_source?.toLowerCase() !== 'affiliate' && !shouldSendUtmsAndBtagsTogether) {
                tags = _pick(tags, [
                    ...btagQueryParams,
                ]);
            }
            this.storage.set(BTAG_SET_DATE, new Date());
        }

        if (tags.affid && !shouldSendUtmsAndBtagsTogether) {
            tags.utm_medium = tags.affid;
            delete tags.affid;
        }
        this.storage.set(MARKETING_DATA, tags);
    }

    private async setMarketingAttributionData(stringifiedAttributionParams: string | null): Promise<void> {
        const acquisitionSourcesLength = await featureFlagService.getFeatureValue(AcquisitionSourcesLength, 10);
        this.storage.set(marketingAttributionData, this.createMarketingAttributionsData(stringifiedAttributionParams, acquisitionSourcesLength));
    }

    private createMarketingAttributionsData(stringifiedAttributionParams: string | null, acquisitionSourcesLength: number): MarketingAttributionData {
        const datedStringifiedAttributionParams = (Math.round((new Date().getTime() / 1000)) + '|' + stringifiedAttributionParams).substring(0, 256);

        let attributions = this.storage.get<MarketingAttributionData>(marketingAttributionData) || [];

        if (attributions.includes(datedStringifiedAttributionParams)) {
            return attributions;
        }

        if (attributions.unshift(datedStringifiedAttributionParams) > acquisitionSourcesLength) {
            attributions = attributions.slice(0, acquisitionSourcesLength);
        }
        return attributions;
    }

    private isBtagExpired(): boolean {
        let marketingData = this.storage.get<MarketingData>(MARKETING_DATA);
        let btagSetDate: string | number = this.storage.get<string>(BTAG_SET_DATE)!;
        if (marketingData?.btag && !btagSetDate) {
            return true;
        }
        try {
            btagSetDate = Date.parse(btagSetDate!);
        } catch (e) {
            return true;
        }
        // Is btag expired calculation
        return !!marketingData?.btag && isBeforeDate(btagSetDate as number, subtractDays(expiresDaysMarketingSourceTags, new Date()));
    }

    private static getQuery(urlTags: QueryDict<UtmQueryParam & BtagQueryParam>): GetQuery {
        let isActionable = true;
        let queryToString = new URLSearchParams(urlTags as QueryDict<UtmQueryParam & BtagQueryParam>).toString();

        if (!queryToString && document.referrer) {
            queryToString = 'referrer=' + document.referrer.replace(/^https?:\/\//, '');

            if (new URL(document.referrer).hostname === window.location.hostname) {
                isActionable = false;
            }
        }

        return { queryToString, isActionable };
    }

    public getPostViewData(): MarketingData {
        let cleverCookie: any;

        try {
            const cookieValue = getCookie('clever');
            if (cookieValue) {
                cleverCookie = JSON.parse(cookieValue)?.getParams;
            }
        } catch (e) {
            console.error('Error parsing clever cookie:', e);
            cleverCookie = {};
        }

        const getValue = (key: string) => cleverCookie?.[key]?.value || '';

        this.postViewData = {
            btag: getValue('btag'),
            affid: getValue('affid'),
            utm_medium: getValue('utm_medium'),
            utm_source: getValue('utm_source'),
            utm_campaign: getValue('utm_campaign'),
            utm_content: getValue('utm_content'),
            utm_term: getValue('utm_term'),
        };
        return this.postViewData;
    }
}

export const marketingSourceService = new MarketingSourceService();
